import { getIsAuthenticated } from '../../../../domain/hidden-data-provider';
import { useEffect, useState } from 'react';
import { getQueryString, getReturnURL } from '../../../../domain/url-provider';
import { getEmailPreferences, setEmailPreferences, unsubscribe } from '../../dal/data-provider';
import { DEFAULT_ERROR } from '../../../../DAL/data-provider';
import { IResponse } from '../../../../interfaces/common-interfaces';
import { IEmailPreference } from '../interfaces';
import Preloader from '../../../../ui/svg/common/preloader';

const COMMON_ERR = 100;
const CONTACT_NOT_LOGGED_IN = 101;
const HARD_BOUNCE = 102;

/**
 * Email Preferences
 * https://dev13.waves.com/email-preferences?guid=9C12F487-58F3-478A-AC28-9D01F2557A49
 * https://wavesaudio.atlassian.net/browse/DSWEB-1837
 * https://wavesaudio.atlassian.net/browse/DSWEB-48
 */
const EmailPreferencesApp = () => {

    const [recipientGUID] = useState(getQueryString()['guid']);
    const [loggedIn] = useState(getIsAuthenticated());
    const [showEmailPreferences] = useState(loggedIn || (!loggedIn && !!recipientGUID));

    const [isLoading, setIsLoading] = useState(false);
    const [preferences, setPreferences] = useState<IEmailPreference[]>([]);
    const [error, setError] = useState(0);
    const [showUnsubscribeOKMessage, setShowUnsubscribeOKMessage] = useState(false);


    // handle unsubscribe option
    // https://wavesaudio.atlassian.net/browse/DSWEB-359
    const [hasUnsubscribe, setHasUnsubscribe] = useState(getQueryString().hasOwnProperty('unsub'));
    const [unsubscribed, setUnsubscribed] = useState(false);

    /**
     * handle server error
     * returns true in case of error
     */
    const handleErrors = (response: IResponse) => {

        if(!response || response.statusCode !== 200 || !response.data) {
            setError(COMMON_ERR);
            return true;
        }

        const data = response.data;

        if(!data.relatedData){
            setError(COMMON_ERR);
            return true;
        }

        const isValid = data.isValid;

        if(!isValid){
            const errorCode = data.relatedData.errorCode;

            switch(errorCode){
                case 'ESPContactNotLogined': {
                    setError(CONTACT_NOT_LOGGED_IN);
                    break;
                }

                case -3: {
                    setError(HARD_BOUNCE);
                    break;
                }

                default: {
                    setError(COMMON_ERR);
                    break;
                }
            }
        }

        return !isValid;
    };

    /**
     * get data on page load
     */
    useEffect(() => {
        (async () => {
            if(!showEmailPreferences) return;

            setIsLoading(true);

            const response = await getEmailPreferences(recipientGUID);

            setIsLoading(false);

            if(handleErrors(response)) return;

            setPreferences(response.data.relatedData.preferences || []);
        })();
    }, [
        showEmailPreferences,
        recipientGUID,
    ]);

    /**
     * on checkbox change
     */
    const handleCheckboxChange = async (preference: IEmailPreference, isChecked: boolean) => {

        setError(0);
        setShowUnsubscribeOKMessage(false);

        const preferencesCopy = [...preferences];

        const foundIndex = preferencesCopy.findIndex(item => item.newsletterID === preference.newsletterID);
        if(foundIndex === -1) return;

        const foundCopy = {...preferencesCopy[foundIndex]};
        foundCopy.isLoading = true;
        preferencesCopy[foundIndex] = foundCopy;

        setPreferences(preferencesCopy);

        const response = await setEmailPreferences(preference.recipientGUID, preference.newsletterID, isChecked);

        if(handleErrors(response)){

            // remove item preloader in case of a server error
            foundCopy.isLoading = false;
            preferencesCopy[foundIndex] = foundCopy;
            setPreferences(preferencesCopy);
            return;
        }

        setPreferences(response.data.relatedData.preferences || []);
    };

    /**
     * unsubscribe from all newsletters
     */
    const unsubscribeHandler = async () => {
        setError(0);
        setShowUnsubscribeOKMessage(false);

        // remove the unsub querystring from URL
        const unsub = getQueryString()['unsub'];
        const updatedURL = window.location.href.replace(`unsub=${encodeURIComponent(unsub)}`, '');
        window.history.replaceState({}, '', updatedURL);

        const response = await unsubscribe(unsub, recipientGUID);
        if(handleErrors(response)) return;

        setPreferences(response.data.relatedData.preferences || []);

        setShowUnsubscribeOKMessage(true);
        setHasUnsubscribe(false);
    };

    return (
        <div>
            {
                !showEmailPreferences &&
                <div className="msg-box my-8">
                    <p>
                        In order to customize your e-mail preferences, please <a href="/create-account" title="">sign up</a> or <a href={ getReturnURL() } title="">log in to your account</a>.
                    </p>
                </div>
            }

            {
                showEmailPreferences &&
                <>
                    {
                        isLoading &&
                        <div className="flex justify-center">
                            <Preloader width="25px" height="25px" color="#4f4f4f" />
                        </div>
                    }

                    {
                        !isLoading && error === 0 && (
                            <div>
                                <p className="text-lg">I would like to receive email updates on the following:</p>

                                {
                                    preferences && preferences.length > 0 &&
                                   <>
                                       <ul className="mt-4 mb-10">
                                           {
                                               preferences.map(preference => {
                                                   return (
                                                       <li key={ preference.newsletterID } className="mb-2 flex items-center">
                                                           <label className="flex items-center">

                                                               <input
                                                                   type="checkbox"
                                                                   className="mr-2"
                                                                   checked={ preference.isSubscribed }
                                                                   onChange={async evt => {
                                                                       await handleCheckboxChange(preference, evt.target.checked);
                                                                   }}
                                                               />

                                                               { preference.newsletterDisplayName }

                                                               {
                                                                   preference.isLoading &&
                                                                   <div className="mx-2"><Preloader width="15px" height="15px" color="#1d1d1d" /></div>
                                                               }

                                                           </label>
                                                       </li>
                                                   )
                                               })
                                           }

                                           {
                                               hasUnsubscribe && (
                                                   <li>
                                                       <label className="flex items-center">

                                                           <input
                                                               type="radio"
                                                               className="mr-2"
                                                               checked={ unsubscribed }
                                                               onChange={async evt => {
                                                                   setUnsubscribed(true);
                                                               }}
                                                           />

                                                           Unsubscribe

                                                           {
                                                               unsubscribed && (
                                                                   <div className="mx-4">
                                                                       <b className="text-red mr-2">Are you sure?</b>

                                                                       <button type="button" className="text-blue-200 font-bold inline-block underline" onClick={async evt => {
                                                                           evt.preventDefault();
                                                                           evt.stopPropagation();
                                                                           await unsubscribeHandler();

                                                                       }}>Yes</button>

                                                                       <span className="inline-block mx-2">/</span>

                                                                       <button type="button" className="text-blue-200 font-bold inline-block underline" onClick={evt => {
                                                                           evt.preventDefault();
                                                                           evt.stopPropagation();

                                                                           setUnsubscribed(false);
                                                                       }}>No</button>
                                                                   </div>
                                                               )
                                                           }

                                                           {/*
                                                               preference.isLoading &&
                                                               <div className="mx-2"><Preloader width="15px" height="15px" color="#1d1d1d" /></div>
                                                           */}

                                                       </label>

                                                       {
                                                           unsubscribed &&
                                                           <div className="ml-5">You can also reduce the numbers of emails you get, by selecting only some of the options above.</div>
                                                       }
                                                   </li>
                                               )
                                           }
                                       </ul>

                                       {
                                           preferences[0].subscriptionStatusID === 6 &&
                                           <p className="error-box mb-6">We had a problem delivering your email. Please
                                               check your email address below. To re-subscribe, select the emails you
                                               wish to receive.</p>
                                       }

                                       <p>Your Email: <b>{ preferences[0].emailAddress }</b></p>

                                       {
                                           !recipientGUID &&
                                           <p>To change your email: <a href="/account/edit-personal-details" title="">Edit Personal Details</a></p>
                                       }
                                   </>
                                }

                                {
                                    showUnsubscribeOKMessage && (
                                        <div className="ok-box my-4">You successfully unsubscribed from all our newsletters.<br /><b>Remember:</b> You can check any of the above options to re-subscribe only to those newsletters.</div>
                                    )
                                }

                            </div>
                        )
                    }

                    {
                        !isLoading && error !== 0 && (
                            <div className="error-box" role="alert">
                                { error === COMMON_ERR && <p>{ DEFAULT_ERROR }</p>}

                                { error === CONTACT_NOT_LOGGED_IN && <p>In order to customize your e-mail preferences, please <a href="/create-account" title="">sign up</a> or <a href="/login/?returnurl=%2femail-preferences" title="">log in to your account</a>.</p>}

                                { error === HARD_BOUNCE && <p>We cannot send newsletters to this email address. This may be because the address is invalid or permanently blocks newsletters ("hard bounce"), or because you have filed a spam complaint in the past.
                                    Please <a href="/account/edit-personal-details" title="">provide a different email address</a>, or <a href="/contact-sales" title="">contact our sales team</a>.</p>}
                            </div>
                        )
                    }

                </>
            }
        </div>
    )
};

export default  EmailPreferencesApp;